"use client";

import Image from "next/image";
import type React from "react";

interface QueryExample {
  imageSrc: string;
  imageAlt: string;
  useCase: string;
  query: string;
  response: string;
}

// Real examples from solutions pages with actual API outputs
const queryExamples: QueryExample[] = [
  {
    imageSrc: "/images/robbery_example.jpeg",
    imageAlt: "Security camera footage",
    useCase: "Security Monitoring",
    query: "what is happening?",
    response: "a man is trying to break into a white car",
  },
  {
    imageSrc: "/images/solutions/extract-receipts/digital_invoice_example.png",
    imageAlt: "Digital invoice",
    useCase: "Document Extraction",
    query: "return as json: total, merchant, item",
    response: '{ "total": 20.0, "merchant": "Anthropic, PBC", "item": "Claude Pro" }',
  },
  {
    imageSrc: "/images/man_ppe.jpg",
    imageAlt: "Construction worker with hard hat",
    useCase: "Safety Compliance",
    query: "what is the worker doing?",
    response: "Inspecting equipment while wearing a hard hat, noting details on a clipboard.",
  },
  {
    imageSrc: "/images/solutions/generate-media-tags/json-tags.jpeg",
    imageAlt: "Golden Gate Bridge",
    useCase: "Media Tagging",
    query: "provide 5 tags",
    response: '["Golden Gate Bridge", "suspension bridge", "orange", "water", "sunrise"]',
  },
];

const QueryCard: React.FC<{ example: QueryExample; priority?: boolean }> = ({
  example,
  priority = false,
}) => {
  return (
    <div className="group relative w-full overflow-hidden rounded-2xl aspect-[4/3]">
      {/* Background Image */}
      <Image
        fill
        src={example.imageSrc}
        alt={example.imageAlt}
        sizes="(min-width: 1024px) 50vw, 100vw"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        priority={priority}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

      {/* Use case label */}
      <div className="absolute top-4 left-4">
        <span className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-white bg-white/15 backdrop-blur-md rounded-full border border-white/20 shadow-lg">
          {example.useCase}
        </span>
      </div>

      {/* Separate Glassmorphism containers for Query and Response */}
      <div className="absolute bottom-4 left-4 right-4 flex flex-col gap-2">
        {/* Query container */}
        <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 px-3 py-2 shadow-lg">
          <div className="flex items-center gap-2">
            <span className="text-purple-300 text-[10px] font-semibold uppercase tracking-wider shrink-0">Query</span>
            <code className="text-white/90 text-sm font-mono truncate">
              &quot;{example.query}&quot;
            </code>
          </div>
        </div>

        {/* Response container */}
        <div className="bg-white/15 backdrop-blur-md rounded-lg border border-white/25 px-3 py-2 shadow-lg">
          <div className="flex items-start gap-2">
            <span className="text-green-300 text-[10px] font-semibold uppercase tracking-wider shrink-0 mt-0.5">Response</span>
            <code className="text-white font-medium text-sm font-mono leading-relaxed">
              {example.response}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

const Demos: React.FC = () => {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
      {queryExamples.map((example, index) => (
        <QueryCard
          key={index}
          example={example}
          priority={index === 0}
        />
      ))}
    </div>
  );
};

export { Demos };

